/**
 * Chat "message received" sound — the same tone on both sides of a chat, so a
 * student hearing a psychologist's reply and a psychologist hearing a student's
 * message get the identical cue. Deliberately NOT the portal-helpline
 * incoming-call ringtone: a call tone means "answer now", a chat ping does not.
 *
 * Synthesized with the Web Audio API rather than shipping an audio file: it
 * keeps the bundle asset-free and borrows no copyrighted system sound. A rising
 * two-note motif with overlapping bell-like decays reads as a notification
 * rather than a click.
 *
 * Tuning lives in NOTE_PEAK / NOTES below — adjust those two to change loudness
 * or length; nothing else in the file needs to move.
 */

const MUTE_KEY = 'elixaa:chat-sound-muted';

let audioCtx: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (audioCtx) return audioCtx;
  try {
    audioCtx = new AudioContext();
  } catch {
    // Unsupported or blocked (also the jsdom path in unit tests) — stay silent
    // rather than throwing into a realtime event handler.
    return null;
  }
  return audioCtx;
}

/** Muted is opt-in and per-browser; absent key = sound on. */
export function isChatSoundMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    // Private mode / storage blocked: fall back to audible.
    return false;
  }
}

export function setChatSoundMuted(muted: boolean): void {
  try {
    window.localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    // Preference just won't persist; the in-session toggle still works.
  }
}

/**
 * Browsers hold an AudioContext suspended until a real user gesture, so the
 * very first inbound message would be silent. Call this from an interaction to
 * prime it.
 */
export function unlockChatSound(): void {
  const ctx = getContext();
  if (ctx?.state === 'suspended') void ctx.resume();
}

/** Peak gain per note. Well above the original 0.16, which was inaudible over
 *  laptop speakers in a room with any ambient noise. */
const NOTE_PEAK = 0.45;

/** Rising two-note motif (E5 -> A5). Overlapping decays give it a bell-like
 *  tail rather than a click, and it lands ~700ms total so it registers instead
 *  of vanishing. */
const NOTES = [
  { freq: 659.25, at: 0, dur: 0.5 },
  { freq: 880, at: 0.13, dur: 0.55 },
] as const;

/** Chime for an inbound chat message. No-ops when muted. */
export function playChatPop(): void {
  if (isChatSoundMuted()) return;
  const ctx = getContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();

  const now = ctx.currentTime;

  // Shared bus so the two overlapping notes can't sum past 1.0 and clip.
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.8, now);
  master.connect(ctx.destination);

  for (const note of NOTES) {
    const start = now + note.at;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(note.freq, start);

    // Exponential ramps can't touch 0, hence the 0.0001 floor.
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(NOTE_PEAK, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + note.dur);

    osc.connect(gain);
    gain.connect(master);
    osc.start(start);
    osc.stop(start + note.dur + 0.02);
  }
}
